/* Owns the top bar of PANEL.md §4: the mobile nav trigger, the `ServerSwitcher`,
   the Ctrl+K palette trigger, the TR/EN toggle and the user menu. The language
   toggle writes `users.locale` through `PATCH /v1/users/me` *and*
   localStorage['sac.lang'], so the choice survives both a reload on this device
   and a sign-in on another one. */

import { LOCALES, type Locale } from '@sentinelac/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Languages, LogOut, Menu, Search, UserRound } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'

import { cn } from '../components/ui/cn.js'
import { pushToast } from '../components/ui/Toast.js'
import { useLang, useT } from '../i18n/index.js'
import { clearAuth, useAuthState } from '../lib/auth-store.js'
import { qk } from '../lib/queryKeys.js'
import { wsClient } from '../lib/ws.js'
import { logout as logoutRequest, patchLocale } from './api.js'
import { ServerSwitcher } from './ServerSwitcher.js'

export interface TopbarProps {
  onOpenSidebar: () => void
  onOpenPalette: () => void
}

export function Topbar({ onOpenSidebar, onOpenPalette }: TopbarProps): ReactNode {
  const t = useT()
  const { lang, setLang } = useLang()
  const { user } = useAuthState()
  const client = useQueryClient()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const localeMutation = useMutation({
    mutationFn: (locale: Locale) => patchLocale(locale),
    onSuccess: (response) => {
      client.setQueryData(qk.me(), (old: unknown) =>
        old && typeof old === 'object' && 'servers' in old ? { ...old, user: response.user } : old,
      )
    },
    onError: () => {
      // The UI language has already switched locally; only the server-side
      // preference failed, so say so instead of reverting under the user.
      pushToast({ variant: 'warn', title: t('errors.INTERNAL') })
    },
  })

  const logout = useMutation({
    mutationFn: logoutRequest,
    // Settled, not success: a failed logout still has to end the local session,
    // otherwise a network blip leaves a signed-in tab behind.
    onSettled: () => {
      wsClient.close()
      clearAuth()
      client.clear()
      void navigate('/login', { replace: true })
    },
  })

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: MouseEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const toggleLang = (): void => {
    const next: Locale = lang === LOCALES[0] ? LOCALES[1] : LOCALES[0]
    setLang(next)
    localeMutation.mutate(next)
  }

  const menuLinkClass =
    'flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-text-muted hover:bg-panel hover:text-text focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-accent md:min-h-9'

  return (
    <header className="flex items-center gap-2 border-b border-border bg-panel px-4 py-2 lg:px-6" data-testid="topbar">
      <button
        type="button"
        onClick={onOpenSidebar}
        aria-label={t('common.openMenu')}
        className="rounded-md p-2 text-text-muted hover:bg-elevated hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:hidden"
      >
        <Menu aria-hidden className="size-5" />
      </button>

      <Link
        to="/servers"
        className="hidden text-base font-semibold text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:block"
      >
        {t('common.appName')}
      </Link>

      <ServerSwitcher />

      <button
        type="button"
        onClick={onOpenPalette}
        aria-keyshortcuts="Control+K Meta+K"
        data-testid="palette-trigger"
        className="ml-auto flex min-h-11 items-center gap-2 rounded-md border border-border-strong bg-surface px-3 text-sm text-text-faint hover:bg-elevated hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:min-h-9"
      >
        <Search aria-hidden className="size-4" />
        <span className="hidden sm:inline">{t('common.commandPalette')}</span>
        <kbd className="hidden rounded border border-border px-1 font-mono text-xs md:inline">Ctrl K</kbd>
      </button>

      <button
        type="button"
        onClick={toggleLang}
        aria-label={t('common.language')}
        className="flex min-h-11 items-center gap-1 rounded-md px-2 text-sm text-text-muted hover:bg-elevated hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:min-h-9"
      >
        <Languages aria-hidden className="size-4" />
        <span className="font-mono text-xs uppercase">{lang}</span>
      </button>

      <div ref={menuRef} className="relative">
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={t('common.userMenu')}
          onClick={() => setMenuOpen((value) => !value)}
          data-testid="user-menu-trigger"
          className="flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-text-muted hover:bg-elevated hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:min-h-9"
        >
          <UserRound aria-hidden className="size-4" />
          <span className="hidden max-w-32 truncate md:inline">{user?.displayName ?? ''}</span>
        </button>

        {menuOpen ? (
          <div
            role="menu"
            aria-label={t('common.userMenu')}
            className="absolute right-0 z-40 mt-1 w-56 rounded-lg border border-border-strong bg-elevated p-1 shadow-pop"
          >
            <Link role="menuitem" to="/account" onClick={() => setMenuOpen(false)} className={menuLinkClass}>
              {t('nav.account')}
            </Link>
            <Link role="menuitem" to="/account/api-keys" onClick={() => setMenuOpen(false)} className={menuLinkClass}>
              {t('nav.apiKeys')}
            </Link>
            <Link role="menuitem" to="/account/plan" onClick={() => setMenuOpen(false)} className={menuLinkClass}>
              {t('nav.plan')}
            </Link>
            <Link role="menuitem" to="/lookup" onClick={() => setMenuOpen(false)} className={menuLinkClass}>
              {t('nav.lookup')}
            </Link>
            {user?.isPlatformAdmin === true ? (
              <Link
                role="menuitem"
                to="/admin/global-bans"
                onClick={() => setMenuOpen(false)}
                className={menuLinkClass}
              >
                {t('nav.adminGlobalBans')}
              </Link>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false)
                logout.mutate()
              }}
              className={cn(menuLinkClass, 'w-full text-left')}
            >
              <LogOut aria-hidden className="size-4" />
              {t('nav.logout')}
            </button>
          </div>
        ) : null}
      </div>
    </header>
  )
}
